import { SuggestionItem } from ".";

/**
 * Recently inserted mentions, so the no-query view opens with things you actually
 * touched rather than whatever the API happened to return first.
 *
 * Scoped per user: the app does not clear localStorage on logout, so an unscoped
 * key would show one user's recent records to the next person on the same browser.
 */
const KEY_PREFIX = "veiligh:mention-recents";
const MAX_RECENTS = 5;

const keyFor = (userId: string | undefined) =>
  `${KEY_PREFIX}:${userId || "anonymous"}`;

/** Identity of a suggestion across reloads. `id` alone is enough; label is for display. */
export interface RecentMention {
  id: string;
  label: string;
  reference?: string;
  modelType?: string;
}

export const loadRecents = (userId: string | undefined): RecentMention[] => {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENTS) : [];
  } catch {
    // Corrupt or unavailable storage must never break the editor.
    return [];
  }
};

export const recordRecent = (
  userId: string | undefined,
  child: SuggestionItem,
): void => {
  if (!child.id) return;
  try {
    const entry: RecentMention = {
      id: child.id,
      label: child.label,
      reference: child.reference,
      modelType: child.modelType,
    };
    const next = [
      entry,
      ...loadRecents(userId).filter((r) => r.id !== child.id),
    ].slice(0, MAX_RECENTS);
    window.localStorage.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    // Storage full or blocked — recents are a convenience, not a requirement.
  }
};

/** Clear on logout. Exported so the auth flow can call it. */
export const clearRecents = (userId: string | undefined): void => {
  try {
    window.localStorage.removeItem(keyFor(userId));
  } catch {
    /* no-op */
  }
};

/**
 * Order a category's children so recently used ones come first, preserving the
 * original order for everything else. Only used when there is no active query —
 * a real search must rank by relevance, not by history.
 */
export const applyRecency = (
  children: SuggestionItem[],
  recents: RecentMention[],
): SuggestionItem[] => {
  if (recents.length === 0) return children;
  const rank = new Map(recents.map((r, i) => [r.id, i]));
  const recent: SuggestionItem[] = [];
  const rest: SuggestionItem[] = [];
  for (const child of children) {
    if (child.id && rank.has(child.id)) recent.push(child);
    else rest.push(child);
  }
  recent.sort((a, b) => (rank.get(a.id!) ?? 0) - (rank.get(b.id!) ?? 0));
  return [...recent, ...rest];
};
